/**
 * Bridge between the API client and the in-app second factor dialog.
 *
 * Routes behind the `passkey.confirm` middleware answer HTTP 423 with a
 * challenge payload when the session has no recent passkey/OTP confirmation.
 * `requestJson` hands that payload to whichever UI registered itself here
 * (features/second-factor-confirm), waits for the user to confirm or cancel,
 * and retries the original request on success.
 *
 * Kept in `shared/api` so the client never imports a feature; the dialog
 * registers itself on mount and unregisters on unmount.
 */

export interface SecondFactorOtpClient {
    id: number;
    name: string;
    last_used_at?: number | string | null;
}

export interface SecondFactorChallenge {
    message?: string;
    require_passkey: boolean;
    require_otp: boolean;
    mfa?: {
        otp_available?: boolean;
        otp_clients?: SecondFactorOtpClient[];
        locked_otp_client?: SecondFactorOtpClient | null;
    };
    ttl_minutes?: number;
}

/** Resolves `true` once the user confirmed, `false` if they cancelled. */
export type SecondFactorHandler = (challenge: SecondFactorChallenge) => Promise<boolean>;

let handler: SecondFactorHandler | null = null;
let inFlight: Promise<boolean> | null = null;

export const SECOND_FACTOR_REQUIRED_STATUS = 423;

export function isSecondFactorChallenge(status: number, payload: unknown): payload is SecondFactorChallenge {
    if (status !== SECOND_FACTOR_REQUIRED_STATUS || !payload || typeof payload !== 'object') return false;
    const p = payload as Record<string, unknown>;
    return typeof p.require_passkey === 'boolean' || typeof p.require_otp === 'boolean';
}

/** Register the UI that can run a confirmation. Returns an unregister function. */
export function setSecondFactorHandler(next: SecondFactorHandler): () => void {
    handler = next;
    return () => {
        if (handler === next) handler = null;
    };
}

/**
 * Ask the registered UI to confirm the second factor. Concurrent callers (two
 * requests rejected at once) share a single confirmation instead of stacking
 * dialogs. Resolves `false` when no UI is mounted.
 */
export function requestSecondFactorConfirmation(challenge: SecondFactorChallenge): Promise<boolean> {
    if (!handler) return Promise.resolve(false);
    if (inFlight) return inFlight;

    const run = handler(challenge)
        .catch(() => false)
        .finally(() => {
            if (inFlight === run) inFlight = null;
        });
    inFlight = run;
    return run;
}

/** Test helper: drop any registered handler and pending confirmation. */
export function resetSecondFactorBridge(): void {
    handler = null;
    inFlight = null;
}
